import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar, Check, ArrowRight, AlertTriangle, Ban, X, ExternalLink } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import FulfillmentBlockedModal from './FulfillmentBlockedModal';
import NotInterestedDialog from './NotInterestedDialog';

const BlockingCommitmentsModal = ({ open, onClose, commitments, onResolved, userRole }) => {
  const navigate = useNavigate();
  const [movingId, setMovingId] = useState(null);
  const [datePickerOpenId, setDatePickerOpenId] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [moveNote, setMoveNote] = useState('');
  const [fulfillmentBlockedOpen, setFulfillmentBlockedOpen] = useState(false);
  const [selectedForFulfillment, setSelectedForFulfillment] = useState(null);
  const [niDialogOpen, setNiDialogOpen] = useState(false);
  const [niTargetCommitment, setNiTargetCommitment] = useState(null);
  // Fulfillment dialog state
  const [fulfillDialogOpen, setFulfillDialogOpen] = useState(false);
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [fulfillForm, setFulfillForm] = useState({ closure_done: 0, revenue_done: 0 });
  const [fulfilling, setFulfilling] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const getTodayString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleMoveToToday = async (commitmentId) => {
    const today = getTodayString();
    setMovingId(commitmentId);
    try {
      await axiosInstance.post(`/commitments/${commitmentId}/move`, {
        to_date: today,
        note: 'Moved to today to resolve blocking commitment'
      });
      toast.success('Commitment moved to today');
      onResolved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move commitment');
    } finally {
      setMovingId(null);
    }
  };

  const handleMoveToDate = async (commitmentId) => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const trimmedNote = moveNote.trim();
    if (trimmedNote.length < 20) {
      toast.error('Reason must be at least 20 characters');
      return;
    }

    setMovingId(commitmentId);
    try {
      await axiosInstance.post(`/commitments/${commitmentId}/move`, {
        to_date: selectedDate,
        note: trimmedNote
      });
      toast.success('Commitment moved successfully');
      setDatePickerOpenId(null);
      setSelectedDate('');
      setMoveNote('');
      onResolved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move commitment');
    } finally {
      setMovingId(null);
    }
  };

  const handleMarkFulfilled = (commitment) => {
    // Check if commitment is verified by both TL and Manager
    if (!commitment.commitment_tl_verified || !commitment.commitment_mgr_verified) {
      setSelectedForFulfillment(commitment);
      setFulfillmentBlockedOpen(true);
      return;
    }
    // Open fulfillment dialog for verified commitments
    setFulfillTarget(commitment);
    setFulfillForm({
      closure_done: commitment.closure_done || 0,
      revenue_done: commitment.revenue_done || 0
    });
    setFulfillDialogOpen(true);
  };

  const handleFulfillSubmit = async () => {
    if (!fulfillTarget) return;
    
    // Validation
    if (fulfillForm.closure_done > fulfillTarget.closure_expected) {
      toast.error(`Closure done cannot exceed expected (${fulfillTarget.closure_expected})`);
      return;
    }
    if (fulfillForm.closure_done > 0 && fulfillForm.revenue_done <= 0) {
      toast.error('Revenue is required when closure is greater than 0');
      return;
    }

    setFulfilling(true);
    try {
      await axiosInstance.patch(`/commitments/${fulfillTarget.id}`, fulfillForm);
      toast.success('Commitment updated successfully');
      setFulfillDialogOpen(false);
      setFulfillTarget(null);
      setFulfillForm({ closure_done: 0, revenue_done: 0 });
      onResolved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commitment');
    } finally {
      setFulfilling(false);
    }
  };

  const openDatePicker = (commitmentId) => {
    setDatePickerOpenId(commitmentId);
    setSelectedDate('');
    setMoveNote('');
  };

  const closeDatePicker = () => {
    setDatePickerOpenId(null);
    setSelectedDate('');
    setMoveNote('');
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!commitments || commitments.length === 0) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Resolve Past Commitments
              <Badge variant="secondary" className="ml-2">
                {commitments.length}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-700 mb-2">
              You have <strong>{commitments.length}</strong> unfulfilled commitment{commitments.length === 1 ? '' : 's'} from past dates blocking new pulse entries.
              Resolve each one by choosing an action below:
            </p>
            <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
              <li><strong>Move to Today</strong> -- reschedule the commitment to today's date</li>
              <li><strong>Move to Date</strong> -- pick a future date (requires a reason, min 20 chars)</li>
              <li><strong>Mark Fulfilled</strong> -- record actual closures/revenue (requires TL + Manager verification)</li>
              <li><strong>Not Interested</strong> -- mark as NI (requires manager approval to clear)</li>
            </ul>
          </div>

          {/* Scrollable commitment list */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-2" style={{ maxHeight: '60vh' }}>
            {commitments.map((commitment) => {
              const moveCount = commitment.movements?.length || 0;
              const moveLimitReached = moveCount >= 3;
              // Use can_move from backend if available, otherwise calculate locally
              // Admin can always move regardless of limit
              const canMove = commitment.can_move !== undefined
                ? (commitment.can_move || userRole === 'admin')
                : (!moveLimitReached || userRole === 'admin');
              const isDatePickerOpen = datePickerOpenId === commitment.id;

              return (
                <div
                  key={commitment.id}
                  className="border rounded-lg p-4 bg-white shadow-sm"
                >
                  {/* Commitment header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{commitment.gym_name}</h4>
                      <p className="text-sm text-gray-500">{commitment.pulse_name} - {commitment.source_name}</p>
                    </div>
                    <Badge
                      variant={
                        commitment.commitment_status === 'not_interested' ? 'outline' :
                        commitment.commitment_status === 'pending' ? 'secondary' : 'outline'
                      }
                      className={
                        commitment.commitment_status === 'not_interested' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                        commitment.commitment_status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                      }
                    >
                      {commitment.commitment_status === 'not_interested' ? 'NI - Awaiting Approval' :
                       commitment.commitment_status === 'pending' ? 'Pending' : 'Partial'}
                    </Badge>
                  </div>

                  {/* Commitment details */}
                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <span className="ml-2 font-medium">{formatCurrency(commitment.revenue_expected)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Closures:</span>
                      <span className="ml-2 font-medium">{commitment.closure_expected}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Original Date:</span>
                      <span className="ml-2">{formatDate(commitment.original_date || commitment.entry_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Current Date:</span>
                      <span className="ml-2">{formatDate(commitment.current_date || commitment.entry_date)}</span>
                    </div>
                  </div>

                  {/* Move count indicator */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        Moved {moveCount}/3 times
                      </span>
                      {moveLimitReached && userRole === 'admin' && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                          Admin Override
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-blue-600 hover:text-blue-800 h-6 px-2"
                      onClick={() => {
                        onClose();
                        navigate(`/commitment-dashboard?filter=pending&datePreset=all`);
                      }}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View in Dashboard
                    </Button>
                  </div>

                  {/* Not Interested Status Message */}
                  {commitment.commitment_status === 'not_interested' && !commitment.ni_approved && (
                    <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-md">
                      <div className="flex items-start gap-2 mb-2">
                        <Ban className="w-4 h-4 text-purple-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-purple-900 mb-1">
                            Marked as Not Interested
                          </p>
                          {commitment.not_interested_reason && (
                            <p className="text-xs text-purple-700 mb-2">
                              Reason: {commitment.not_interested_reason}
                            </p>
                          )}
                          <p className="text-xs text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">
                            <strong>Action Required:</strong> This commitment is waiting for manager approval.
                            Contact your manager to approve this NI request so it stops blocking pulse entries.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  {!isDatePickerOpen && commitment.commitment_status !== 'not_interested' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {canMove ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMoveToToday(commitment.id)}
                            disabled={movingId === commitment.id}
                          >
                            <ArrowRight className="w-4 h-4 mr-1" />
                            {movingId === commitment.id ? 'Moving...' : 'Move to Today'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDatePicker(commitment.id)}
                          >
                            <Calendar className="w-4 h-4 mr-1" />
                            Move to Date
                          </Button>
                        </>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <Ban className="w-3 h-3 mr-1" />
                          3 moves reached - Fulfill only
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleMarkFulfilled(commitment)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Mark Fulfilled
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => {
                          setNiTargetCommitment(commitment);
                          setNiDialogOpen(true);
                        }}
                        title="Mark as Not Interested"
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        Not Interested
                      </Button>
                    </div>
                  )}

                  {/* Inline date picker */}
                  {isDatePickerOpen && (
                    <div className="mt-3 p-3 bg-gray-50 border rounded-md">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium">Select Target Date</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={closeDatePicker}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={getTodayString()}
                        className="mb-3"
                      />
                      <div className="mb-3">
                        <div className="flex justify-between items-center mb-1">
                          <Label className="text-sm">
                            Reason <span className="text-red-500">*</span>
                          </Label>
                          <span className={`text-xs ${moveNote.trim().length >= 20 ? 'text-green-600' : 'text-gray-500'}`}>
                            {moveNote.trim().length}/20 min
                          </span>
                        </div>
                        <Textarea
                          value={moveNote}
                          onChange={(e) => setMoveNote(e.target.value)}
                          placeholder="Explain why this commitment is being moved (minimum 20 characters)"
                          rows={2}
                          className={moveNote.trim().length > 0 && moveNote.trim().length < 20 ? 'border-red-300' : ''}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={closeDatePicker}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleMoveToDate(commitment.id)}
                          disabled={!selectedDate || moveNote.trim().length < 20 || movingId === commitment.id}
                        >
                          {movingId === commitment.id ? 'Moving...' : 'Confirm Move'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter className="mt-4 pt-4 border-t flex-col sm:flex-row gap-2">
            <Button
              variant="default"
              className="bg-[#1F4E79] hover:bg-[#163a5c] flex-1 sm:flex-none"
              onClick={() => {
                onClose();
                navigate('/commitment-dashboard?filter=pending&datePreset=all&dateFilter=past');
              }}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Go to Commitment Dashboard
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fulfillment Blocked Modal */}
      <FulfillmentBlockedModal
        open={fulfillmentBlockedOpen}
        onClose={() => {
          setFulfillmentBlockedOpen(false);
          setSelectedForFulfillment(null);
        }}
        commitment={selectedForFulfillment}
        currentUser={{ role: userRole }}
        onVerificationComplete={() => {
          setFulfillmentBlockedOpen(false);
          setSelectedForFulfillment(null);
          onResolved();
        }}
      />

      {/* Not Interested Dialog */}
      <NotInterestedDialog
        open={niDialogOpen}
        onOpenChange={setNiDialogOpen}
        commitment={niTargetCommitment}
        onSuccess={() => {
          setNiTargetCommitment(null);
          onResolved();
        }}
      />

      {/* Fulfillment Dialog */}
      <Dialog open={fulfillDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setFulfillDialogOpen(false);
          setFulfillTarget(null);
          setFulfillForm({ closure_done: 0, revenue_done: 0 });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Update Commitment Fulfillment
            </DialogTitle>
          </DialogHeader>

          {fulfillTarget && (
            <div className="py-4 space-y-4">
              <div className="bg-gray-50 rounded-md p-3 text-sm">
                <p className="font-medium">{fulfillTarget.gym_name}</p>
                <p className="text-gray-600">{fulfillTarget.pulse_name} • {fulfillTarget.entry_date}</p>
                <p className="text-gray-600 mt-1">
                  Expected: {fulfillTarget.closure_expected} closure(s) • ₹{parseFloat(fulfillTarget.revenue_expected || 0).toLocaleString('en-IN')}
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="closure_done">Closures Done (max: {fulfillTarget.closure_expected})</Label>
                  <Input
                    id="closure_done"
                    type="number"
                    min="0"
                    max={fulfillTarget.closure_expected}
                    value={fulfillForm.closure_done}
                    onChange={(e) => setFulfillForm({ ...fulfillForm, closure_done: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <Label htmlFor="revenue_done">Revenue Done (₹)</Label>
                  <Input
                    id="revenue_done"
                    type="number"
                    min="0"
                    value={fulfillForm.revenue_done}
                    onChange={(e) => setFulfillForm({ ...fulfillForm, revenue_done: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFulfillDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleFulfillSubmit} disabled={fulfilling}>
              {fulfilling ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BlockingCommitmentsModal;
